import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { PublishedWithChanges, RotateLeft } from '@material-symbols-svg/react/rounded/w500';
import { useState } from 'react';

import { DiscardPendingPrompt } from '../../components';
import { palette, tokens, useAppButtonStyles, useSurfaceStyles } from '../../theme';

export type PendingActionsCardProps = {
    count: number;
    onApply: () => void;
    onReset: () => void;
};

export const PendingActionsCard: React.FC<PendingActionsCardProps> = ({ count, onApply, onReset }) => {
    const classes = useStyles();
    const surfaces = useSurfaceStyles();
    const buttons = useAppButtonStyles();
    const [resetPromptOpen, setResetPromptOpen] = useState(false);
    const disabled = count === 0;

    const handleConfirmReset = () => {
        onReset();
        setResetPromptOpen(false);
    };

    return (
        <section className={mergeClasses(surfaces.card, classes.card)}>
            <div className={classes.cardTitle}>
                Pending log entries <span className={classes.countPill}>{count}</span>
            </div>

            <div className={classes.actionRow}>
                <button type="button" className={buttons.primary} disabled={disabled} onClick={onApply}>
                    <PublishedWithChanges className={classes.buttonIcon} aria-hidden />
                    <span>Apply{count > 0 ? ` (${count})` : ''}</span>
                </button>

                <button type="button" className={buttons.secondary} disabled={disabled} onClick={() => setResetPromptOpen(true)}>
                    <RotateLeft className={classes.buttonIcon} aria-hidden />
                    <span>Reset</span>
                </button>
            </div>

            {resetPromptOpen && (
                <DiscardPendingPrompt
                    title="Reset pending entries?"
                    count={count}
                    confirmLabel="Reset"
                    onConfirm={handleConfirmReset}
                    onCancel={() => setResetPromptOpen(false)}
                />
            )}
        </section>
    );
};

const useStyles = makeStyles({
    card: {
        padding: '22px',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
    },
    cardTitle: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '16px',
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
    },
    countPill: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '24px',
        height: '24px',
        padding: '0 9px',
        borderRadius: palette.pillRadius,
        backgroundColor: palette.accentBg,
        fontSize: '12px',
        fontWeight: tokens.fontWeightSemibold,
        color: palette.accentText,
    },
    actionRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
    },
    buttonIcon: {
        width: '16px',
        height: '16px',
    },
});
